import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './GuildWar.css';
import { useI18n } from '../i18n';
import { getContentPage } from '../content/publicContent';
import ContentSections from './ContentSections';

const API_BASE = 'https://rangers-api-3mfw.onrender.com';

const RAID_UI = {
  ja: {
    character: 'キャラ',
    skill1: 'スキル1',
    skill2: 'スキル2',
    collecting: '収集中...',
    lastCollected: '最終収集',
    characterCount: 'キャラクター',
    collectError: 'エラー',
    notCollected: 'データ未収集',
    loadError: 'データの取得中にエラーが発生しました。後でもう一度お試しください。',
    loading: 'データ読み込み中...',
    empty: 'データがまだ収集されていません。',
    dateLocale: 'ja-JP'
  },
  ko: {
    character: '캐릭터',
    skill1: '스킬 1',
    skill2: '스킬 2',
    collecting: '수집 중...',
    lastCollected: '마지막 수집',
    characterCount: '캐릭터',
    collectError: '오류',
    notCollected: '수집된 데이터가 없습니다',
    loadError: '데이터를 가져오는 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
    loading: '데이터를 불러오는 중...',
    empty: '아직 수집된 데이터가 없습니다.',
    dateLocale: 'ko-KR'
  },
  'zh-tw': {
    character: '角色',
    skill1: '技能 1',
    skill2: '技能 2',
    collecting: '收集中...',
    lastCollected: '最後收集',
    characterCount: '角色',
    collectError: '錯誤',
    notCollected: '尚未收集資料',
    loadError: '取得資料時發生錯誤。請稍後再試。',
    loading: '資料載入中...',
    empty: '尚未收集資料。',
    dateLocale: 'zh-TW'
  },
  th: {
    character: 'ตัวละคร',
    skill1: 'สกิล 1',
    skill2: 'สกิล 2',
    collecting: 'กำลังเก็บข้อมูล...',
    lastCollected: 'เก็บข้อมูลล่าสุด',
    characterCount: 'ตัวละคร',
    collectError: 'ข้อผิดพลาด',
    notCollected: 'ยังไม่มีข้อมูลที่เก็บ',
    loadError: 'เกิดข้อผิดพลาดระหว่างดึงข้อมูล กรุณาลองใหม่ภายหลัง',
    loading: 'กำลังโหลดข้อมูล...',
    empty: 'ยังไม่มีข้อมูลที่เก็บ',
    dateLocale: 'th-TH'
  },
  en: {
    character: 'Character',
    skill1: 'Skill 1',
    skill2: 'Skill 2',
    collecting: 'Collecting...',
    lastCollected: 'Last collected',
    characterCount: 'characters',
    collectError: 'Error',
    notCollected: 'No collection data yet',
    loadError: 'Failed to load data. Please try again later.',
    loading: 'Loading data...',
    empty: 'No data has been collected yet.',
    dateLocale: 'en-US'
  }
};

const SKILL_NAMES = {
  ja: {
    STUN: 'スタン',
    MOVE_ENEMY: 'ノックバック',
    MOVE_ENEMY2: '後方移動',
    CLEANING_ENEMY: '敵バフ解除',
    CLEAN_DOTHEAL: '継続回復解除',
    CLEANING_UNBEAT: '無敵解除（X秒）',
    CLEAN_UNBEAT: '無敵解除',
    ATTRIBUTE_CHANGE: '属性変更',
    SLOW: '移動速度ダウン',
    SLEEP: '睡眠',
    SILENCE: '沈黙',
    BLIND: '盲目',
    DEBUFF: 'デバフ'
  },
  ko: {
    STUN: '스턴',
    MOVE_ENEMY: '넉백',
    MOVE_ENEMY2: '뒤로 이동',
    CLEANING_ENEMY: '적 버프 해제',
    CLEAN_DOTHEAL: '지속 회복 해제',
    CLEANING_UNBEAT: '무적 해제 (X초)',
    CLEAN_UNBEAT: '무적 해제',
    ATTRIBUTE_CHANGE: '속성 변경',
    SLOW: '이동 속도 감소',
    SLEEP: '수면',
    SILENCE: '침묵',
    BLIND: '실명',
    DEBUFF: '디버프'
  },
  'zh-tw': {
    STUN: '暈眩',
    MOVE_ENEMY: '擊退',
    MOVE_ENEMY2: '向後移動',
    CLEANING_ENEMY: '解除敵方增益',
    CLEAN_DOTHEAL: '解除持續恢復',
    CLEANING_UNBEAT: '解除無敵（X秒）',
    CLEAN_UNBEAT: '解除無敵',
    ATTRIBUTE_CHANGE: '屬性變更',
    SLOW: '移動速度降低',
    SLEEP: '睡眠',
    SILENCE: '沉默',
    BLIND: '失明',
    DEBUFF: '減益'
  },
  th: {
    STUN: 'สตัน',
    MOVE_ENEMY: 'ผลักถอย',
    MOVE_ENEMY2: 'ย้ายไปด้านหลัง',
    CLEANING_ENEMY: 'ลบบัฟศัตรู',
    CLEAN_DOTHEAL: 'ลบการฟื้นฟูต่อเนื่อง',
    CLEANING_UNBEAT: 'ลบสถานะอมตะ (X วินาที)',
    CLEAN_UNBEAT: 'ลบสถานะอมตะ',
    ATTRIBUTE_CHANGE: 'เปลี่ยนธาตุ',
    SLOW: 'ลดความเร็วเคลื่อนที่',
    SLEEP: 'หลับ',
    SILENCE: 'ใบ้',
    BLIND: 'ตาบอด',
    DEBUFF: 'ดีบัฟ'
  },
  en: {
    STUN: 'Stun',
    MOVE_ENEMY: 'Knockback',
    MOVE_ENEMY2: 'Backward movement',
    CLEANING_ENEMY: 'Enemy buff removal',
    CLEAN_DOTHEAL: 'Heal-over-time removal',
    CLEANING_UNBEAT: 'Invincibility removal (X sec)',
    CLEAN_UNBEAT: 'Invincibility removal',
    ATTRIBUTE_CHANGE: 'Attribute change',
    SLOW: 'Movement speed down',
    SLEEP: 'Sleep',
    SILENCE: 'Silence',
    BLIND: 'Blind',
    DEBUFF: 'Debuff'
  }
};

const BUFF_FACTOR_NAMES = {
  ja: {
    SKILL_AVOID_CHANCE: 'スキル回避率ダウン',
    SKILL_RESIST: 'スキル抵抗ダウン',
    SKILL_EFFICIENCY: 'スキル効率ダウン',
    ATK: '攻撃力ダウン',
    DEF: '防御力ダウン',
    ATK_SPEED: '攻撃速度ダウン',
    MOVE_SPEED: '移動速度ダウン'
  },
  ko: {
    SKILL_AVOID_CHANCE: '스킬 회피율 감소',
    SKILL_RESIST: '스킬 저항 감소',
    SKILL_EFFICIENCY: '스킬 효율 감소',
    ATK: '공격력 감소',
    DEF: '방어력 감소',
    ATK_SPEED: '공격 속도 감소',
    MOVE_SPEED: '이동 속도 감소'
  },
  'zh-tw': {
    SKILL_AVOID_CHANCE: '技能迴避率降低',
    SKILL_RESIST: '技能抵抗降低',
    SKILL_EFFICIENCY: '技能效率降低',
    ATK: '攻擊力降低',
    DEF: '防禦力降低',
    ATK_SPEED: '攻擊速度降低',
    MOVE_SPEED: '移動速度降低'
  },
  th: {
    SKILL_AVOID_CHANCE: 'ลดอัตราหลบสกิล',
    SKILL_RESIST: 'ลดต้านทานสกิล',
    SKILL_EFFICIENCY: 'ลดประสิทธิภาพสกิล',
    ATK: 'ลดพลังโจมตี',
    DEF: 'ลดพลังป้องกัน',
    ATK_SPEED: 'ลดความเร็วโจมตี',
    MOVE_SPEED: 'ลดความเร็วเคลื่อนที่'
  },
  en: {
    SKILL_AVOID_CHANCE: 'Skill evasion down',
    SKILL_RESIST: 'Skill resistance down',
    SKILL_EFFICIENCY: 'Skill efficiency down',
    ATK: 'Attack down',
    DEF: 'Defense down',
    ATK_SPEED: 'Attack speed down',
    MOVE_SPEED: 'Movement speed down'
  }
};

const RaidSkillCheck = () => {
  const { locale } = useI18n();
  const pageContent = getContentPage(locale, 'raidHittingCharacters');
  const ui = RAID_UI[locale] || RAID_UI.en;
  const skillNames = SKILL_NAMES[locale] || SKILL_NAMES.en;
  const buffFactorNames = BUFF_FACTOR_NAMES[locale] || BUFF_FACTOR_NAMES.en;
  const [characters, setCharacters] = useState([]);
  const [collectStatus, setCollectStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    fetchHittingCharacters();
    fetchCollectStatus();
  }, []);

  const fetchHittingCharacters = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await axios.get(
        `${API_BASE}/api/guild-war-guardian/hitting-characters`
      );
      setCharacters(response.data.characters || []);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCollectStatus = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/guild-war-guardian/collect-status`
      );
      setCollectStatus(response.data);
    } catch {
      // ステータス取得失敗は無視
    }
  };

  const formatSkill = (skill) => {
    if (skill.skillType === 'DEBUFF' && skill.buffFactorType) {
      return buffFactorNames[skill.buffFactorType] || skill.buffFactorType;
    }
    const baseName = skillNames[skill.skillType] || skill.skillType;
    if (skill.effectTime != null) {
      if (baseName.includes('X')) {
        return baseName.replace('X', skill.effectTime);
      }
      return locale === 'ja' ? `${baseName}（${skill.effectTime}秒）` : `${baseName} (${skill.effectTime}s)`;
    }
    return baseName;
  };

  const formatSkills = (skills = []) => {
    return skills.length > 0 ? skills.map(formatSkill).join(', ') : '-';
  };

  const getSortSkillLabel = (char) => {
    const skill = [...(char.skills1 || []), ...(char.altSkills || [])][0];
    return skill ? formatSkill(skill) : '';
  };

  const sortedCharacters = [...characters].sort((a, b) => {
    const skillCompare = getSortSkillLabel(a).localeCompare(
      getSortSkillLabel(b),
      locale,
      { sensitivity: 'base' }
    );

    if (skillCompare !== 0) return skillCompare;
    return (a.unitCode || '').localeCompare(b.unitCode || '');
  });

  const getRangerDetailUrl = (unitCode) => {
    return `https://rangers.lerico.net/${locale}/ranger/${unitCode}`;
  };

  return (
    <div className="guild-war-container">
      <h1>{pageContent.title}</h1>
      {pageContent.lead && <p>{pageContent.lead}</p>}
      <ContentSections
        sections={pageContent.sections}
        className="tool-context"
        collapsible
        summaryLabel={pageContent.summaryLabel}
      />

      {collectStatus && (
        <div className="collect-status">
          {collectStatus.isCollecting ? (
            <p>{ui.collecting}</p>
          ) : collectStatus.lastCollectedAt ? (
            <p>
              {ui.lastCollected}: {new Date(collectStatus.lastCollectedAt).toLocaleString(ui.dateLocale)}
              {' / '}
              {collectStatus.totalCount} {ui.characterCount}
              {collectStatus.error && <span className="auth-error"> ({ui.collectError}: {collectStatus.error})</span>}
            </p>
          ) : (
            <p>{ui.notCollected}</p>
          )}
        </div>
      )}

      {isError ? (
        <p>{ui.loadError}</p>
      ) : isLoading ? (
        <p>{ui.loading}</p>
      ) : characters.length === 0 ? (
        <p>{ui.empty}</p>
      ) : (
        <table className="deck-table">
          <thead>
            <tr>
              <th>{ui.character}</th>
              <th>{ui.skill1}</th>
              <th>{ui.skill2}</th>
            </tr>
          </thead>
          <tbody>
            {sortedCharacters.map((char, index) => (
              <tr key={index}>
                <td>
                  <a
                    href={getRangerDetailUrl(char.unitCode)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={char.unitCode}
                  >
                    <img
                      src={`https://rangers.lerico.net/res/${char.unitCode}/${char.unitCode}-thum.png`}
                      alt={char.unitCode}
                      className="unit-img"
                    />
                  </a>
                </td>
                <td>{formatSkills(char.skills1)}</td>
                <td>{formatSkills(char.altSkills)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RaidSkillCheck;
